import { type NextRequest, NextResponse } from 'next/server';
import { pathToRegexp } from 'path-to-regexp';
import { isAuthenticated } from '../server/auth';
import type { ConditionalMiddleware } from './types';

export type WhenFn = (args: { req: NextRequest }) => boolean | Promise<boolean>;

/**
 * A configuration for conditional middleware-based redirects
 */
export type Redirect =
  | {
      /**
       * The incoming request path pattern to redirect from. It uses [path-to-regexp](https://github.com/pillarjs/path-to-regexp) to support similar pattern matching to Next.js' redirects config ([docs](https://nextjs.org/docs/pages/api-reference/next-config-js/redirects#path-matching)).
       */
      source: string;
      /**
       * The path or URL to redirect to.
       *
       * Note that this does not currently support matched parameters from `source` like [Next does](https://nextjs.org/docs/pages/api-reference/next-config-js/redirects#path-matching).
       */
      destination: string;
      /**
       * A function that accepts a `req` request object, and should return `true` when the request should be redirected.
       */
      when: WhenFn;
      /**
       * (Optional) A callback that runs when the redirect is executed, e.g. for logging or writing headers/cookies to the response. Accepts the `req` request and `res` response objects as arguments.
       */
      onRedirect?: OnRedirectFn;
    }
  | {
      /**
       * The incoming request path pattern to redirect from. It uses [path-to-regexp](https://github.com/pillarjs/path-to-regexp) to support similar pattern matching to Next.js' redirects config ([docs](https://nextjs.org/docs/pages/api-reference/next-config-js/redirects#path-matching)).
       */
      source: string;
      /**
       * A function that accepts a `req` request object, and returns the destination path to redirect to, or otherwise `null`.
       */
      redirectTo: (args: {
        req: NextRequest;
      }) => string | null | Promise<string | null>;
      /**
       * (Optional) A callback that runs when the redirect is executed, e.g. for logging or writing headers/cookies to the response. Accepts the `req` request and `res` response objects as arguments.
       */
      onRedirect?: OnRedirectFn;
    };

/**
 * A configuration for redirecting unauthenticated requests
 */
export type UnauthenticatedRedirect = {
  /**
   * The incoming request path pattern to redirect from. It uses [path-to-regexp](https://github.com/pillarjs/path-to-regexp) to support similar pattern matching to Next.js' redirects config ([docs](https://nextjs.org/docs/pages/api-reference/next-config-js/redirects#path-matching)).
   */
  source: string;
  /**
   * The path or URL to redirect to.
   *
   * Note that this does not currently support matched parameters from `source` like [Next does](https://nextjs.org/docs/pages/api-reference/next-config-js/redirects#path-matching).
   */
  destination?: string;
  /**
   * (Optional) A callback that runs when the redirect is executed, e.g. for logging or writing headers/cookies to the response. Accepts the `req` request and `res` response objects as arguments.
   */
  onRedirect?: OnRedirectFn;
};

// If we have multiple redirect matches based on `source`, we choose to run these in serial instead of parallel,
// to give us more control over avoiding expensive operations (e.g. queries)
/* eslint-disable no-await-in-loop */
const findMatchingRedirect = async (
  req: NextRequest,
  redirects: Redirect[],
): Promise<{ destination: string; redirect: Redirect } | null> => {
  for (const redirect of redirects) {
    const sourceRegexp = pathToRegexp(redirect.source);
    const pathMatch = sourceRegexp.exec(req.nextUrl.pathname);

    if (pathMatch) {
      if ('redirectTo' in redirect) {
        const destination = await redirect.redirectTo({ req });

        if (destination) {
          return { destination, redirect };
        }
      }

      if ('when' in redirect) {
        if (await redirect.when({ req })) {
          return { destination: redirect.destination, redirect };
        }
      }
    }
  }

  return null;
};
/* eslint-enable no-await-in-loop */

const buildRedirectUrl = (destination: string, req: NextRequest): URL => {
  const url = new URL(destination, req.url);

  // Forward all query params from the requested URL to the redirect URL, (ex:
  // utm_* params that are used for tracking) that are not already present in
  // the redirect URL.
  req.nextUrl.searchParams.forEach((value, key) => {
    if (!url.searchParams.has(key)) {
      url.searchParams.set(key, value);
    }
  });

  return url;
};

export const createRedirectsMiddleware = (
  redirects?: Redirect[],
): ConditionalMiddleware => {
  return async (req: NextRequest) => {
    if (redirects) {
      const matchingRedirect = await findMatchingRedirect(req, redirects);

      if (matchingRedirect) {
        const { destination, redirect } = matchingRedirect;

        const destinationUrl = buildRedirectUrl(destination, req);
        const res = NextResponse.redirect(destinationUrl);

        await redirect.onRedirect?.({ req, res });

        return res;
      }
    }

    return undefined;
  };
};

export type OnRedirectFn = (args: {
  req: NextRequest;
  res: NextResponse;
}) => void | Promise<void>;

// The name of the return URL cookie is defined in Rails and used by
// the `client-facing-auth` gem
export const RET_COOKIE_NAME = 'ret';

/**
 * Sets the `ret` cookie, for the client to be redirected back to their original requested
 * URL after login.
 */
const setReturnCookie: OnRedirectFn = ({ req, res }) => {
  res.cookies.set(RET_COOKIE_NAME, req.nextUrl.href, {
    domain: '.stitchfix.com',
  });
};

/**
 * Redirects an unauthenticated request. Returns a redirect configuration for usage with
 * `createMiddleware`.
 *
 * Redirects to `/login` by default (overridable with `destination`), and sets the `ret` cookie to
 * return the client back to the original page after login.
 *
 * @example
 * // src/middleware.page.ts
 * export const middleware = createMiddleware({
 *   redirects: [
 *     redirectWhenUnauthenticated({
 *       source: '/account',
 *       onRedirect: ({ req, res }) => console.log('redirected')
 *     }),
 *   ],
 * });
 */
export const redirectWhenUnauthenticated = ({
  source,
  destination,
  onRedirect,
}: UnauthenticatedRedirect): Redirect => {
  return {
    source,
    destination: destination || '/login',
    onRedirect: async args => {
      await setReturnCookie(args);
      await onRedirect?.(args);
    },
    when: ({ req }) => !isAuthenticated(req),
  };
};
